import React, { useState, forwardRef, useRef, useImperativeHandle, useCallback, useEffect } from "react";
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import SaleEntriesExtraModal from "../../modals/sale-entries-extras-modal";
import { useForm } from "react-hook-form";
import Commons from '../../../util/commons'
import SimpleObjectRenderer from "../aggrid-cell-renderers/simple-object-renderer";
import DoubleValRenderer from "../aggrid-cell-renderers/doubleval-renderer";
import ProductEditor from "../aggrid-cell-editors/product-editor";
import MUEditor from "../aggrid-cell-editors/mu-editor";
import VatEditor from "../aggrid-cell-editors/vat-editor";
import ActionsRenderer from "../aggrid-cell-renderers/actions-renderer";
import { useEditableGrid } from './useEditableGrid';
import DoubleValueSetter from '../DoubleValueSetter';
import DoubleEditor from '../aggrid-cell-editors/double-editor';

// Utility: Tax calculation
// vatIsAmount=true → vat is a fixed euro amount (not a percentage)
const calculateTaxAmounts = (unitPrice, quantity, vat, discount, vatIsAmount = false) => {
    const q = quantity === "-" ? 1 : parseFloat(quantity);
    const discountPercent = discount ? parseFloat(discount) : 0;

    let priceWithoutVat = parseFloat(unitPrice) * q;
    priceWithoutVat -= discountPercent * priceWithoutVat / 100;

    let unitPriceWithVat, priceWithVat, vatAmount;

    if (vatIsAmount) {
        const fixedVat = vat === "-" ? 0 : parseFloat(vat);
        priceWithVat = priceWithoutVat + fixedVat;
        unitPriceWithVat = q > 0 ? priceWithVat / q : parseFloat(unitPrice);
        vatAmount = fixedVat;
    } else {
        const vatPercent = vat === "-" ? 0 : parseFloat(vat);
        unitPriceWithVat = parseFloat(unitPrice) * (1 + vatPercent / 100);
        priceWithVat = unitPriceWithVat * q;
        priceWithVat -= discountPercent * priceWithVat / 100;
        vatAmount = priceWithVat - priceWithoutVat;
    }

    return {
        unitPriceWithVat: parseFloat(unitPriceWithVat).toFixed(4),
        priceWithoutVat: parseFloat(priceWithoutVat).toFixed(2),
        priceWithVat: parseFloat(priceWithVat).toFixed(2),
        vatAmount: parseFloat(vatAmount).toFixed(2),
    };
};

const EMPTY_ROW = {
    product: { value: undefined, label: undefined },
    description: undefined,
    quantity: undefined,
    price: undefined,
    measurementUnit: undefined
};

// Empty row: consider valid so no red highlight on the template row
const isValid = (data) => {
    const hasAnyValue =
        data.unitPrice != null ||
        data.quantity != null ||
        data.product?.value != null;

    if (!hasAnyValue) return true;

    const basicValid = (
        data.unitPrice != null &&
        data.quantity != null &&
        data.product?.value != null
    );

    if (!basicValid) return false;

    if (data.extraLineFields && data.extraLineFields.length > 0) {
        const quantities = data.quantity === "-" ? 1 : parseFloat(data.quantity);
        const discountVal = data.discount ? parseFloat(data.discount) : 0;
        const lineprice = data.unitPrice * quantities - discountVal * (data.unitPrice * quantities) / 100;

        const extraRowTotals = data.extraLineFields.reduce((total, e) => {
            let rowamount = 0;
            if (e.percent && data.unitPrice && data.quantity) {
                const q = data.quantity === "-" ? 1 : data.quantity;
                const _lp = parseFloat(data.unitPrice * q * e.percentAmount / 100).toFixed(2);
                rowamount = parseFloat(_lp - (data.discount || 0) * _lp / 100).toFixed(2);
            } else if (e.percentAmount) {
                rowamount = parseFloat(e.percentAmount).toFixed(2);
            } else if (e.amount) {
                rowamount = parseFloat(e.amount).toFixed(2);
            }
            return total + parseFloat(rowamount);
        }, 0);

        if (parseFloat(extraRowTotals).toFixed(2) !== parseFloat(lineprice).toFixed(2)) {
            return false;
        }
    }

    return true;
};


const SaleEntries = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { getValues } = useForm();
    const refSalesExtraEntries = useRef();
    const initRowDataRef = useRef(null);
    const recalcDebounceRef = useRef(null);
    const reCalculateTotalsRef = useRef(props.reCalculateTotals);
    reCalculateTotalsRef.current = props.reCalculateTotals;

    const [totalsError, setTotalsError] = useState("");

    // State for the per-row extra modal data only (gridApi/hasError live in the hook)
    const [extraState, setExtraState] = useState({
        extraRowIndex: false,
        extraRowData: [],
        extraTaxes: [],
        vatCategoryException: [],
        rowTotals: 0.00,
        rowTotalsMess: "",
        rowTotalsConstraintDisplay: "none"
    });

    const { gridApi, setGridApi, hasError, rowStyle, getRowStyle, addRow, checkErrors, getItems, setItems } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
    });

    const debouncedReCalculateTotals = useCallback(() => {
        clearTimeout(recalcDebounceRef.current);
        recalcDebounceRef.current = setTimeout(() => {
            reCalculateTotalsRef.current();
        }, 100);
    }, []);

    useEffect(() => {
        return () => clearTimeout(recalcDebounceRef.current);
    }, []);

    const onGridReady = useCallback(params => {
        setGridApi(params.api);
        let data = params.api.gos.gridOptions.rowData;
        if (!data) data = [];
        const newData = [...data, { ...EMPTY_ROW }];
        params.api.setRowData(newData);
    }, [setGridApi]);

    useImperativeHandle(ref, () => ({
        getData() {
            return { entries: getItems() };
        },
        setData(data) {
            setItems(data);
        },
        getNotes() {
            return getValues().notes;
        },
        clearData() {
            setItems([]);
            debouncedReCalculateTotals();
        },
        hasError() {
            return hasError;
        }
    }));

    const setRowNodeDataValue = useCallback((rowNode, name, value) => {
        if (!rowNode || !rowNode.data) return;
        if (rowNode.data[name] != value) {
            rowNode.setDataValue(name, value);
            rowNode.data[name] = value;
        }
    }, []);

    const onCellValueChanged = useCallback((params) => {
        setTimeout(() => {
            const nodeIndex = params.node.rowIndex;
            const rowNode = params.node; // Use params.node directly instead of re-fetching
            const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
            const data = params.node.data;

            if (nodeIndex === countDisplayedRows && params.newValue !== undefined) {
                const colid = params.column.colId;
                if ((colid === "product" || colid === "vat" || colid === "measurementUnit")
                    && params.newValue.value !== undefined && params.newValue.value !== "") {
                    addRow(params.api);
                } else if (colid !== "product" && colid !== "vat" && colid !== "measurementUnit") {
                    addRow(params.api);
                }
            }

            if (params.column !== undefined) {
                const colid = params.column.colId;

                switch (colid) {
                    case "vat":
                    case "quantity":
                    case "discount":
                    case "unitPrice":
                    case "measurementUnit": {

                        const vatModel = data.vat?.model;
                        const vatRaw = vatModel?.value ?? data.vat?.label ?? "0";
                        const vat = vatRaw === "-" ? 0 : vatRaw;
                        const vatIsAmount = vatModel?.isAmount === true;
                        const quantities = data.quantity === "-" ? 1 : parseFloat(data.quantity);
                        const discountVal = data.discount ? parseFloat(data.discount) : 0;

                        const taxData = calculateTaxAmounts(data.unitPrice, quantities, vat, discountVal, vatIsAmount);
                        setRowNodeDataValue(rowNode, "priceWithoutVat", taxData.priceWithoutVat);
                        setRowNodeDataValue(rowNode, "priceWithVat", taxData.priceWithVat);

                        if (colid === "unitPrice" || colid === "vat") {
                            const upwv1 = parseFloat(taxData.unitPriceWithVat).toFixed(4);
                            const upwv2 = parseFloat(data.unitPriceWithVat || 0).toFixed(4);
                            if (upwv1 !== upwv2) {
                                setRowNodeDataValue(rowNode, "unitPriceWithVat", taxData.unitPriceWithVat);
                            }
                        }

                        if (data.extraLineFields) {
                            const newExtraLineFields = getNewExtraLineFields(data);
                            setRowNodeDataValue(rowNode, "extraLineFields", newExtraLineFields);
                            const extraRowTotals = getExtraRowTotals(data);
                            const lineprice = data.unitPrice * quantities - discountVal * (data.unitPrice * quantities) / 100;

                            if (parseFloat(extraRowTotals).toFixed(2) !== parseFloat(lineprice).toFixed(2)) {
                                setTotalsError(t("classificationSumError"));
                            } else {
                                setTotalsError("");
                            }
                        }

                        break;
                    }
                    case "unitPriceWithVat": {
                        const _vatModel = data.vat?.model;
                        const _vatRaw = _vatModel?.value ?? data.vat?.label ?? "0";
                        const vat = _vatRaw === "-" ? 0 : _vatRaw;
                        const _vatIsAmount = _vatModel?.isAmount === true;
                        const quantities = data.quantity === "-" ? 1 : parseFloat(data.quantity);

                        // fixed amount: unitPriceWithVat includes a fixed vat spread across units
                        const unitPrice = _vatIsAmount
                            ? parseFloat(data.unitPriceWithVat) - parseFloat(vat) / quantities
                            : parseFloat(data.unitPriceWithVat) / (1 + 0.01 * vat);
                        const unitPriceFixed = parseFloat(unitPrice).toFixed(2);
                        const upwv2 = parseFloat(data.unitPrice || 0).toFixed(2);

                        if (unitPriceFixed !== upwv2) {
                            setRowNodeDataValue(rowNode, "unitPrice", unitPriceFixed);
                        }

                        const priceWithVat = parseFloat(data.unitPriceWithVat * quantities).toFixed(2);
                        const discountVal = data.discount ? parseFloat(data.discount) : 0;
                        const finalPrice = parseFloat(priceWithVat - discountVal * priceWithVat / 100).toFixed(2);
                        setRowNodeDataValue(rowNode, "priceWithVat", finalPrice);


                        break;
                    }
                    case "product": {
                        const val = data.product;
                        if (val.model) {
                            setRowNodeDataValue(rowNode, "unitPrice", parseFloat(val.model.price).toFixed(2));

                            if (Commons.isOnGroup(params.colDef.cellRendererParams.invoiceType.myDataInvoiceType, "zeroPrice")) {
                                setRowNodeDataValue(rowNode, "unitPrice", "0.0");
                            }
                            if (Commons.isOnGroup(params.colDef.cellRendererParams.invoiceType.myDataInvoiceType, "noQuantity")) {
                                setRowNodeDataValue(rowNode, "quantity", "-");
                            } else if (rowNode.data.quantity == null) {
                                setRowNodeDataValue(rowNode, "quantity", "1.00");
                            }
                            if (rowNode.data.discount == null) {
                                setRowNodeDataValue(rowNode, "discount", "0.00");
                            }
                            if (rowNode.data.vat == null) {
                                if (Commons.isOnGroup(params.colDef.cellRendererParams.invoiceType.myDataInvoiceType, "noVat")) {
                                    setRowNodeDataValue(rowNode, "vat", { value: "VAT_EMPTY", label: t("VAT_EMPTY"), model: { id: "VAT_EMPTY", value: "-" } });
                                } else if (Commons.isOnGroup(params.colDef.cellRendererParams.invoiceType.myDataInvoiceType, "zeroVat")) {
                                    setRowNodeDataValue(rowNode, "vat", { value: "VAT_0", label: "0", model: { id: "VAT_0", value: "0" } });
                                } else {
                                    setRowNodeDataValue(rowNode, "vat", { value: val.model.vat.id, label: t(val.model.vat.id), model: val.model.vat });
                                }
                            }
                            if (rowNode.data.measurementUnit == null) {
                                if (Commons.isOnGroup(params.colDef.cellRendererParams.invoiceType.myDataInvoiceType, "noMeasurementUnit")) {
                                    setRowNodeDataValue(rowNode, "measurementUnit", { value: "-", label: "-" });
                                } else {
                                    setRowNodeDataValue(rowNode, "measurementUnit", { value: val.model.measurementUnit, label: t(val.model.measurementUnit) });
                                }
                            }

                            if (rowNode.data.taxes == null && val.model.productMoreTaxes) {
                                const pmt = [];
                                val.model.productMoreTaxes.forEach(element => {
                                    let amount = 0;
                                    if (element.deduction) {
                                        element.myDataMoreTax = element.deduction;
                                        if (element.myDataMoreTax.percent) {
                                            const rate = element.myDataMoreTax.amountValue / 100;
                                            const q = rowNode.data.quantity === "-" ? 1 : rowNode.data.quantity;
                                            amount = parseFloat(rowNode.data.unitPrice * q * rate).toFixed(2);
                                        }
                                    }
                                    if (element.amount && !isNaN(parseInt(element.amount))) {
                                        amount = element.amount;
                                    } else if (element.myDataMoreTax?.amountValue?.includes("%")) {
                                        const rate = parseFloat(element.myDataMoreTax.amountValue.replace("%", "")) / 100;
                                        const q = rowNode.data.quantity === "-" ? 1 : rowNode.data.quantity;
                                        amount = parseFloat(rowNode.data.unitPrice * q * rate).toFixed(2);
                                    }

                                    pmt.push({
                                        amount,
                                        myDataMoreTax: {
                                            value: element.myDataMoreTax.type === "DEDUCTION" ? element.myDataMoreTax.id : element.myDataMoreTax.uid,
                                            uid: element.myDataMoreTax.uid,
                                            label: element.myDataMoreTax.type === "DEDUCTION" ? element.myDataMoreTax.name : t("TAX_" + element.myDataMoreTax.uid),
                                            type: element.myDataMoreTax.type,
                                            key: element.myDataMoreTax.key,
                                            amountValue: element.myDataMoreTax.amountValue,
                                            subtracted: element.myDataMoreTax.type === "DEDUCTION" ? element.deduction.subtracted : null
                                        },
                                        id: element.id,
                                        myDataMoreTaxes: params.colDef.cellRendererParams.myDataMoreTaxes,
                                        myDataMoreTaxesTypes: params.colDef.cellRendererParams.myDataMoreTaxesTypes,
                                        type: { label: t(element.myDataMoreTax.type), value: element.myDataMoreTax.type },
                                    });
                                });
                                setRowNodeDataValue(rowNode, "taxes", pmt);
                            }

                            if (rowNode.data.vatCategoryException == null && val.model.vatCategoryException) {
                                const _vatCategoryException = { value: val.model.vatCategoryException, label: t(val.model.vatCategoryException) };
                                setRowNodeDataValue(rowNode, "vatCategoryException", _vatCategoryException);
                            }

                            const vat = val.model.vat.value === "-" ? 0 : val.model.vat.value;
                            const unitPriceWithVat = parseFloat(parseFloat(1 + vat / 100).toFixed(2) * parseFloat(val.model.price).toFixed(2)).toFixed(4);
                            setRowNodeDataValue(rowNode, "unitPriceWithVat", unitPriceWithVat.toString());

                            const _evRaw = rowNode.data.vat ? (rowNode.data.vat.model?.value ?? rowNode.data.vat.label) : "0";
                            const effectiveVat = _evRaw === "-" ? 0 : _evRaw;
                            const effectiveQty = rowNode.data.quantity === "-" ? 1 : parseFloat(rowNode.data.quantity || 1);
                            const effectiveDiscount = rowNode.data.discount ? parseFloat(rowNode.data.discount) : 0;
                            const productTaxData = calculateTaxAmounts(rowNode.data.unitPrice, effectiveQty, effectiveVat, effectiveDiscount);
                            setRowNodeDataValue(rowNode, "priceWithoutVat", productTaxData.priceWithoutVat);
                            setRowNodeDataValue(rowNode, "priceWithVat", productTaxData.priceWithVat);

                        }

                        if (data.product?.model?.productClassifications) {
                            const _classData = data.product.model.productClassifications.map(e => e.productClassification);
                            setExtraState(prev => ({ ...prev, extraRowData: { extraLineFields: _classData } }));
                        }
                        break;
                    }

                }
            }
            params.api.refreshCells({ rowNodes: [params.node], force: true });
            debouncedReCalculateTotals();
            checkErrors(params.api);
        });
    }, [t, addRow, setRowNodeDataValue, debouncedReCalculateTotals, checkErrors]);

    const getNewExtraLineFields = useCallback((data) => {
        return data.extraLineFields.map(e => {
            const nf = { ...e };
            if (nf.percent && data.unitPrice && data.quantity) {
                const q = data.quantity === "-" ? 1 : data.quantity;
                nf.amount = parseFloat(data.unitPrice * q * nf.percentAmount / 100).toFixed(2);
                nf.amount = parseFloat(nf.amount - nf.amount * data.discount / 100).toFixed(2);
            } else if (e.percentAmount) {
                nf.amount = parseFloat(e.percentAmount).toFixed(2);
                nf.amount = parseFloat(nf.amount - nf.amount * (data.discount || 0) / 100).toFixed(2);
            }
            return nf;
        });
    }, []);

    const getExtraRowTotals = useCallback((data) => {
        return data.extraLineFields.reduce((totalRowAmount, e) => {
            let rowamount = 0;
            if (e.percent && data.unitPrice && data.quantity) {
                const q = data.quantity === "-" ? 1 : data.quantity;
                const lineprice = parseFloat(data.unitPrice * q * e.percentAmount / 100).toFixed(2);
                rowamount = parseFloat(lineprice - data.discount * lineprice / 100).toFixed(2);
            } else if (e.percentAmount) {
                rowamount = parseFloat(e.percentAmount).toFixed(2);
            } else if (e.amount) {
                rowamount = parseFloat(e.amount).toFixed(2);
            }
            return totalRowAmount + parseFloat(rowamount);
        }, 0);
    }, []);

    const handleShowExtra = useCallback((rowIndex, rowdata) => {
        setExtraState(prev => ({ ...prev, extraRowIndex: rowIndex }));

        const node = gridApi.getRowNode(rowIndex);
        const quantity = node.data.quantity === "-" ? 1 : node.data.quantity;
        let amount = node.data.unitPrice * quantity;
        const discount = node.data.discount ? parseFloat(node.data.discount) : 0;
        amount -= discount * amount / 100;
        const _amount = parseFloat(amount).toFixed(2);

        if ((!rowdata.extraLineFields || rowdata.extraLineFields.length === 0) &&
            rowdata.product?.model?.productClassifications?.length > 0) {

            const elf = [];
            rowdata.product.model.productClassifications.forEach(e => {
                if (e.invoiceType.id === props.selectedInvoiceType.value) {
                    const types = e.classificationValues;
                    const opts = types.map(element => ({
                        value: element,
                        label: t(element) + " (" + element + ")",
                    }));

                    let _amount = node.data.unitPrice * quantity;
                    if (e.percent) {
                        _amount = parseFloat(_amount * e.amount / 100).toFixed(2);
                    } else {
                        _amount = e.amount;
                    }
                    _amount = parseFloat(_amount - _amount * node.data.discount / 100).toFixed(2);

                    const prefix = e.classificationCategory.replace("CATEGORY_", "") + ") ";
                    elf.push({
                        classificationCategory: { value: e.classificationCategory, label: prefix + t(e.classificationCategory) },
                        classificationValue: { value: e.classificationValue, label: t(e.classificationValue) + " (" + e.classificationValue + ")" },
                        classificationValues: opts,
                        amount: _amount,
                        percent: e.percent,
                        percentAmount: e.amount
                    });
                }
            });

            if (elf.length > 0) {
                rowdata.extraLineFields = elf;
            } else {
                rowdata = addDefaultClassifications(props, rowdata, rowIndex);
            }
        } else if (!rowdata.extraLineFields || rowdata.extraLineFields.length === 0) {
            rowdata = addDefaultClassifications(props, rowdata, rowIndex);
        }

        let _totalsum = 0;
        if (rowdata.extraLineFields) {
            rowdata.extraLineFields.forEach(e => {
                _totalsum += parseFloat(e.amount || 0);
            });
        }

        setExtraState(prev => ({
            ...prev,
            rowTotals: parseFloat(_amount),
            rowTotalsMess: <span>{t("linetotals") + ": " + _amount}</span>,
            rowTotalsConstraintDisplay: _totalsum != _amount ? "unset" : "none"
        }));

        setExtraState(prev => ({ ...prev, extraRowData: rowdata.extraLineFields || [] }));

        if (rowdata.taxes) {
            const groupTaxes = props.moreTaxesOptions.reduce((r, a) => {
                r[a.type] = [...r[a.type] || [], a];
                return r;
            }, {});
            rowdata.taxes.forEach(e => {
                if (!e.myDataMoreTaxes || e.myDataMoreTaxes.length === 0) {
                    e.myDataMoreTaxes = groupTaxes[e.myDataMoreTax.type];
                }
            });
            setExtraState(prev => ({ ...prev, extraTaxes: rowdata.taxes }));
        }

        setExtraState(prev => ({ ...prev, vatCategoryException: rowdata.vatCategoryException || null }));

        initRowDataRef.current = JSON.stringify({
            extraLineFields: rowdata.extraLineFields || null,
            taxes: rowdata.taxes || null,
            vatCategoryException: rowdata.vatCategoryException || null
        });

        refSalesExtraEntries.current.showModal();
    }, [gridApi, props, t]);

    const addDefaultClassifications = useCallback((props, rowdata, rowIndex) => {
        if (props.invoiceClassCategories?.length > 0) {
            const _extraLineFields = [];
            const node = gridApi.getRowNode(rowIndex);
            const q = node.data.quantity === "-" ? 1 : node.data.quantity;

            props.invoiceClassCategories.forEach(category => {
                const types = category.classificationValues;
                const opts = types.map(element => ({
                    value: element,
                    label: t(element) + " (" + element + ")",
                }));

                let _amount = category.percent
                    ? node.data.unitPrice * q * category.amount / 100
                    : category.amount;

                _amount -= _amount * node.data.discount / 100;
                _amount = parseFloat(_amount).toFixed(2);

                if (props.selfPricing === category.selfPricing) {
                    const label1 = category.classificationCategory.replace("CATEGORY_", "") + ") " + t(category.classificationCategory);
                    const label2 = t(category.classificationValue) + " (" + category.classificationValue + ")";

                    _extraLineFields.push({
                        classificationCategory: { value: category.classificationCategory, label: label1 },
                        classificationValue: { value: category.classificationValue, label: label2 },
                        amount: _amount,
                        percent: category.percent,
                        percentAmount: category.amount,
                        classificationValues: opts
                    });
                }
            });

            rowdata.extraLineFields = _extraLineFields;
            return rowdata;
        } else if (props.incomeClassCategories?.[0]) {
            const node = gridApi.getRowNode(rowIndex);
            const q = node.data.quantity === "-" ? 1 : node.data.quantity;
            const types = props.incomeClassCategories[0].classificationValues;
            const opts = types.map(element => ({
                value: element,
                label: t(element) + " (" + element + ")",
            }));

            const label1 = props.incomeClassCategories[0].value.replace("CATEGORY_", "") + ") " + t(props.incomeClassCategories[0].value);
            const label2 = t(props.incomeClassCategories[0].classificationValues[0]) + " (" + props.incomeClassCategories[0].classificationValues[0] + ")";

            let _amount = q * node.data.unitPrice;
            _amount -= _amount * node.data.discount / 100;
            _amount = parseFloat(_amount).toFixed(2);

            rowdata.extraLineFields = [{
                classificationCategory: { value: props.incomeClassCategories[0].value, label: label1 },
                classificationValue: { value: props.incomeClassCategories[0].classificationValues[0], label: label2 },
                amount: _amount,
                classificationValues: opts,
                percent: true,
                percentAmount: 100,
            }];
            return rowdata;
        }
        return rowdata;
    }, [gridApi, props, t]);

    const handleCloseExtra = useCallback((_e, update) => {
        const obj = JSON.parse(initRowDataRef.current);
        const rowNode = gridApi.getRowNode(extraState.extraRowIndex);

        if (update) {
            const _items = refSalesExtraEntries.current.getData();
            setRowNodeDataValue(rowNode, "extraLineFields", _items.saleEntryClassifications);
            setRowNodeDataValue(rowNode, "taxes", _items.taxes);
            setRowNodeDataValue(rowNode, "vatCategoryException", _items.vatCategoryException);
            debouncedReCalculateTotals();
            checkErrors(gridApi);
            let anyMismatch = false;
            gridApi.forEachNode(node => {
                const d = node.data;
                if (!d.extraLineFields || d.extraLineFields.length === 0 || d.unitPrice == null) return;
                const quantities = d.quantity === "-" ? 1 : parseFloat(d.quantity);
                const discountVal = d.discount ? parseFloat(d.discount) : 0;
                const lineprice = d.unitPrice * quantities - discountVal * (d.unitPrice * quantities) / 100;
                const extraRowTotals = getExtraRowTotals(d);
                if (parseFloat(extraRowTotals).toFixed(2) !== parseFloat(lineprice).toFixed(2)) {
                    anyMismatch = true;
                }
            });
            setTotalsError(anyMismatch ? t("classificationSumError") : "");
        } else {
            setRowNodeDataValue(rowNode, "extraLineFields", obj.extraLineFields);
            setRowNodeDataValue(rowNode, "taxes", obj.taxes);
            setRowNodeDataValue(rowNode, "vatCategoryException", obj.vatCategoryException);
        }
        refSalesExtraEntries.current.closeModal();
    }, [gridApi, extraState.extraRowIndex, debouncedReCalculateTotals, checkErrors, getExtraRowTotals, t]);

    const onRemoveRow = useCallback(() => {
        if (gridApi) {
            const rowData = [];
            gridApi.forEachNode(node => rowData.push(node.data));
            rowData.pop();
            debouncedReCalculateTotals();
        }
    }, [gridApi, debouncedReCalculateTotals]);

    const suppressEnter = useCallback((params) => {
        return params.event.key === 'Enter';
    }, []);

    const onCellEditingStarted = useCallback((e) => {
        const field = e.colDef.field;
        const invoiceType = props.selectedInvoiceType.myDataInvoiceType;

        if (field === "vat") {
            if (Commons.isOnGroup(invoiceType, "noVat") || Commons.isOnGroup(invoiceType, "zeroVat")) {
                e.colDef.editable = false;
                gridApi.stopEditing();
            }
        } else if (field === "quantity") {
            if (Commons.isOnGroup(invoiceType, "noQuantity")) {
                e.colDef.editable = false;
                gridApi.stopEditing();
            }
        } else if (field === "measurementUnit") {
            if (Commons.isOnGroup(invoiceType, "noMeasurementUnit")) {
                e.colDef.editable = false;
                gridApi.stopEditing();
            }
        } else if (field === "unitPrice" || field === "unitPriceWithVat" || field === "discount") {
            if (Commons.isOnGroup(invoiceType, "zeroPrice")) {
                e.colDef.editable = false;
                gridApi.stopEditing();
            }
        }
    }, [gridApi, props.selectedInvoiceType]);

    return (
        <div className="col-md-12 product_entries_table_container">
            <div>
                <div className="ag-theme-alpine editableGrid sales-entries-table-container" style={{ width: '100%' }}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        rowHeight={45}
                        style={{ width: '100%', height: '300px' }}
                        onCellValueChanged={onCellValueChanged}
                        onCellEditingStarted={onCellEditingStarted}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        suppressRowTransform={true}
                        singleClickEdit={true}
                        skipHeaderOnAutoSize={true}
                        defaultColDef={{
                            flex: 1,
                            resizable: true,
                            wrapText: false,
                            suppressMovable: true
                        }}
                        columnDefs={[
                            { field: "extraLineFields", hide: true },
                            { field: "taxes", hide: true },
                            { field: "vatCategoryException", hide: true },
                            {
                                cellEditorPopup: true, field: "product", flex: 3, headerName: t("ProductService"), minWidth: 200, headerClass: "required", cellRenderer: SimpleObjectRenderer, cellEditor: ProductEditor, editable: true,
                                suppressKeyboardEvent: (params) => suppressEnter(params),
                                cellRendererParams: {
                                    invoiceType: props.selectedInvoiceType,
                                    myDataMoreTaxesTypes: props.moreTaxesTypesOptions,
                                    myDataMoreTaxes: props.moreTaxesOptions
                                }
                            },
                            { field: "quantity", flex: 0.5, minWidth: 80, headerName: t("Quantity"), headerClass: "required", editable: true, cellRenderer: DoubleValRenderer, cellRendererParams: { precision: 2 }, valueSetter: (params) => DoubleValueSetter(params, { allowDash: true }), cellEditor: DoubleEditor, cellEditorParams: { allowDash: true } },
                            { field: "measurementUnit", headerComponentParams: { template: "<span><i class='fa fa-info-circle percentInfoIcon'></i>" + t("MUN") + "</span>" }, flex: 0.5, minWidth: 60, headerTooltip: t("muInfo"), headerName: t("MUN"), headerClass: "required", options: props.measurementUnits, editable: true, cellEditor: MUEditor, cellRenderer: SimpleObjectRenderer },
                            { field: "unitPrice", flex: 0.5, minWidth: 100, headerName: t("unitPrice"), headerClass: "required", editable: true, cellRenderer: DoubleValRenderer, valueSetter: DoubleValueSetter, cellEditor: DoubleEditor },
                            { field: "vat", headerComponentParams: { template: "<span><i class='fa fa-info-circle percentInfoIcon'></i>" + t("Vat") + "</span>" }, flex: 0.5, minWidth: 60, headerTooltip: t("vatInfo"), headerName: t("Vat"), headerClass: "required", cellRendererParams: { invoiceType: props.selectedInvoiceType }, options: props.vats, editable: true, cellEditor: VatEditor, cellEditorPopup: true, cellRenderer: SimpleObjectRenderer },
                            { field: "unitPriceWithVat", flex: 0.5, minWidth: 100, headerClass: "required", headerName: t("unitPriceWithVat"), editable: true, cellRenderer: DoubleValRenderer, cellRendererParams: { precision: 4 }, valueSetter: DoubleValueSetter, cellEditor: DoubleEditor },
                            { field: "discount", flex: 0.5, minWidth: 76, headerName: t("DiscountRate"), editable: true, cellRenderer: DoubleValRenderer, valueSetter: DoubleValueSetter, cellEditor: DoubleEditor },
                            { field: "priceWithoutVat", flex: 0.5, minWidth: 100, headerName: t("priceWithoutVat") },
                            { field: "priceWithVat", flex: 0.5, minWidth: 90, headerName: t("priceWithVat"), cellStyle: { backgroundColor: "LightBlue" } , cellRenderer: DoubleValRenderer},
                            { field: "name", flex: 0.5, minWidth: 80, headerName: t("Actions"), headerTooltip: t("actionInfo"), headerComponentParams: { template: "<span><i class='fa fa-info-circle percentInfoIcon'></i>" + t("Actions") + "</span>" }, cellRenderer: ActionsRenderer, cellRendererParams: { action: "edit_modal_delete", onRemoveRow: onRemoveRow, onShowExtra: handleShowExtra } },
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
            <span className="row-error-info">{totalsError}</span>
            <SaleEntriesExtraModal ref={refSalesExtraEntries}
                optionVatExceptionCategories={props.vatCategoryExceptions}
                otherTaxesOptions={props.otherTaxes}
                withHoldTaxesOptions={props.withHoldTaxes}
                extraRowData={extraState.extraRowData}
                extraTaxes={extraState.extraTaxes}
                vatCategoryException={extraState.vatCategoryException}
                rowTotalsMess={extraState.rowTotalsMess}
                handleCloseExtra={handleCloseExtra}
                rowTotalsConstraintDisplay={extraState.rowTotalsConstraintDisplay}
                rowTotals={extraState.rowTotals}
                incomeClassCategories={props.incomeClassCategories}
                moreTaxesTypesOptions={props.moreTaxesTypesOptions}
                moreTaxesOptions={props.moreTaxesOptions}
                myDataInvoiceType={props.selectedInvoiceType ? props.selectedInvoiceType.myDataInvoiceType : null}
                isInvoice00_1={props.isInvoice00_1}
            >
            </SaleEntriesExtraModal>
        </div>
    );
});

export default SaleEntries;
